import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  DashboardOutlined,
  PlayCircleOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useHistory, useLocation } from 'umi';
import FeatureDetailCards from '@/components/FeatureDetailCards';
import SignalWaveform from '@/components/SignalWaveform';
import TraitMappingCards from '@/components/TraitMappingCards';
import KeypointPlayer from '@/pages/activity/vitalityGo/KeypointPlayer';
import { getActionDetail } from '@/services/actionLibrary';
import { getMemberTrainingWorkspaceActionDetail } from '@/services/technical';
import ActionInterpretationPanel from './ActionInterpretationPanel';
import {
  RECORD_STATUS_LABELS,
  groupUnitSeriesByPosition,
  groupObservationsByCategory,
  metricName,
  observationValue,
  parseJson,
  success,
  timeText,
} from '../presentation';

const { Paragraph, Text, Title } = Typography;

const CATEGORY_LABELS = {
  mobility: '灵活性',
  strengthPerformance: '力量表现',
  control: '控制能力',
  stability: '稳定性',
  coordination: '协调性',
  agility: '敏捷性',
  powerPerformance: '爆发力',
  other_features: '其他动作表现',
};

const BUSINESS_SUMMARY_METRICS = new Set(['validUnitCount', 'durationMs']);

const METRIC_COLORS = [
  { color: '#1677ff', background: '#e6f4ff' },
  { color: '#2f54eb', background: '#f0f5ff' },
  { color: '#389e0d', background: '#f6ffed' },
  { color: '#d46b08', background: '#fff7e6' },
  { color: '#722ed1', background: '#f9f0ff' },
  { color: '#c41d7f', background: '#fff0f6' },
  { color: '#08979c', background: '#e6fffb' },
];

function renderMetricTiles(items) {
  if (!items.length) return <Empty description="暂无可展示的数据" />;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
      }}
    >
      {items.map((item, index) => (
        <div
          key={item.observationId || index}
          style={{
            padding: '14px 16px',
            borderRadius: 8,
            border: '1px solid #e8e8e8',
            background: '#fafafa',
          }}
        >
          <Text type="secondary" style={{ fontSize: 13 }}>
            {metricName(item)}
          </Text>
          <div style={{ marginTop: 6, fontSize: 20, fontWeight: 600 }}>
            {observationValue(item)}
          </div>
        </div>
      ))}
    </div>
  );
}

function UnitSeriesCards({ rows }) {
  if (!rows.length) return <Empty description="暂无逐次动作指标" />;
  return (
    <Space direction="vertical" size={10} style={{ display: 'flex' }}>
      {rows.map((row) => (
        <div
          key={row.key}
          style={{
            padding: '12px 16px',
            border: '1px solid #e8e8e8',
            borderRadius: 10,
            background: '#fff',
          }}
        >
          <Space wrap style={{ marginBottom: 10 }}>
            <Text strong>周期 {row.position}</Text>
            <Tag>第 {row.position} 次</Tag>
            <Tag color={row.sourceUnitValid ? 'green' : 'default'}>
              {row.sourceUnitValid ? '有效动作' : '未完整识别'}
            </Tag>
          </Space>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {row.metrics.map((metric, metricIndex) => {
              const accent = METRIC_COLORS[metricIndex % METRIC_COLORS.length];
              const available = metric.status === 'available';
              return (
                <div
                  key={`${metric.observationId}-${metricIndex}`}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 6,
                    minWidth: 150,
                    padding: '6px 10px',
                    borderRadius: 6,
                    background: available ? accent.background : '#fafafa',
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {metric.name}
                  </Text>
                  <Text
                    strong
                    style={{
                      color: available ? accent.color : '#bfbfbf',
                      fontSize: 13,
                    }}
                  >
                    {metric.value}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </Space>
  );
}

UnitSeriesCards.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      position: PropTypes.number,
      sourceUnitValid: PropTypes.bool,
      metrics: PropTypes.array,
    }),
  ),
};

UnitSeriesCards.defaultProps = { rows: [] };

function hasAiConclusionContent(conclusion) {
  if (
    !conclusion ||
    typeof conclusion !== 'object' ||
    Array.isArray(conclusion)
  ) {
    return false;
  }
  const overallScore = conclusion.overall_score ?? conclusion.overallScore;
  const hasSummary =
    typeof conclusion.summary === 'string' && conclusion.summary.trim();
  const hasOverallScore =
    overallScore !== null &&
    overallScore !== undefined &&
    String(overallScore).trim();
  const hasDimensions =
    Array.isArray(conclusion.dimensions) &&
    conclusion.dimensions.some(
      (item) => item && typeof item === 'object' && !Array.isArray(item),
    );
  const hasSuggestions =
    Array.isArray(conclusion.suggestions) &&
    conclusion.suggestions.some(
      (item) => typeof item === 'string' && item.trim(),
    );
  return Boolean(
    hasSummary || hasOverallScore || hasDimensions || hasSuggestions,
  );
}

function AiConclusion({ conclusion }) {
  const safeConclusion = conclusion || {};
  const dimensions = Array.isArray(safeConclusion.dimensions)
    ? safeConclusion.dimensions.filter(
        (item) => item && typeof item === 'object',
      )
    : [];
  const suggestions = Array.isArray(safeConclusion.suggestions)
    ? safeConclusion.suggestions.filter(
        (item) => typeof item === 'string' && item,
      )
    : [];
  const overallScore =
    safeConclusion.overall_score ?? safeConclusion.overallScore;
  const hasOverallScore = overallScore !== null && overallScore !== undefined;
  const hasAiConclusion = hasAiConclusionContent(safeConclusion);
  if (!hasAiConclusion) {
    return <Empty description="本次动作暂无可展示的 AI 分析结论" />;
  }
  return (
    <div
      style={{
        padding: '16px 20px',
        borderRadius: 10,
        border: '1px solid #adc6ff',
        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
      }}
    >
      <Space style={{ marginBottom: 12 }}>
        <RobotOutlined style={{ color: '#1890ff', fontSize: 18 }} />
        <Text strong style={{ color: '#1890ff' }}>
          AI 动作分析结论
        </Text>
        {hasOverallScore && <Tag color="blue">综合表现 {overallScore} 分</Tag>}
      </Space>
      {safeConclusion.summary && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 14,
            borderRadius: 8,
            borderLeft: '3px solid #1890ff',
            background: '#fff',
          }}
        >
          <Paragraph style={{ margin: 0, lineHeight: 1.8 }}>
            {safeConclusion.summary}
          </Paragraph>
        </div>
      )}
      {dimensions.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 10,
            marginBottom: suggestions.length ? 14 : 0,
          }}
        >
          {dimensions.map((dimension, index) => (
            <div
              key={`${dimension.name || 'dimension'}-${index}`}
              style={{
                padding: '12px 14px',
                borderRadius: 8,
                border: '1px solid #d6e4ff',
                background: '#fff',
              }}
            >
              <Space wrap>
                <Text strong>{dimension.name || '动作表现'}</Text>
                {dimension.score !== null && dimension.score !== undefined && (
                  <Tag color="blue">{dimension.score} 分</Tag>
                )}
              </Space>
              {dimension.advice && (
                <Paragraph style={{ margin: '8px 0 0' }}>
                  {dimension.advice}
                </Paragraph>
              )}
              {Array.isArray(dimension.observations) &&
                dimension.observations.length > 0 && (
                  <List
                    size="small"
                    header={<Text type="secondary">观察</Text>}
                    dataSource={dimension.observations}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                  />
                )}
              {Array.isArray(dimension.movementGuidance) &&
                dimension.movementGuidance.length > 0 && (
                  <List
                    size="small"
                    header={<Text type="secondary">动作指导</Text>}
                    dataSource={dimension.movementGuidance}
                    renderItem={(item) => (
                      <List.Item>
                        <Text type="success">{item}</Text>
                      </List.Item>
                    )}
                  />
                )}
            </div>
          ))}
        </div>
      )}
      {suggestions.length > 0 && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid #e8e8e8',
            background: '#fff',
          }}
        >
          <Text strong style={{ color: '#fa8c16' }}>
            改进建议
          </Text>
          <List
            size="small"
            dataSource={suggestions}
            renderItem={(item) => <List.Item>{item}</List.Item>}
          />
        </div>
      )}
    </div>
  );
}

AiConclusion.propTypes = {
  conclusion: PropTypes.shape({
    overall_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    overallScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    summary: PropTypes.string,
    dimensions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        advice: PropTypes.string,
        observations: PropTypes.arrayOf(PropTypes.string),
        movementGuidance: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
    suggestions: PropTypes.arrayOf(PropTypes.string),
  }),
};

AiConclusion.defaultProps = {
  conclusion: null,
};

export default function MemberTrainingActionDetail() {
  const history = useHistory();
  const location = useLocation();
  const { evaluationId } = location.query || {};
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [actionName, setActionName] = useState('');
  const [hasTraitMappings, setHasTraitMappings] = useState(null);
  const observation = useMemo(
    () => parseJson(detail?.observationContent),
    [detail?.observationContent],
  );
  const aiConclusion = useMemo(() => {
    const parsed = parseJson(detail?.aiConclusionContent);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  }, [detail?.aiConclusionContent]);
  const interpretation = useMemo(() => {
    const parsed = parseJson(detail?.interpretationContent);
    return parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      parsed.schemaVersion === 'action-interpretation@1'
      ? parsed
      : null;
  }, [detail?.interpretationContent]);
  const hasAiConclusion = hasAiConclusionContent(aiConclusion);
  const observations = observation?.observations || [];
  const summary = observations.filter((item) => item.scope === 'summary');
  const visibleSummary = summary.filter((item) =>
    BUSINESS_SUMMARY_METRICS.has(item.metricCode),
  );
  const diagnosticSummary = summary.filter(
    (item) => !BUSINESS_SUMMARY_METRICS.has(item.metricCode),
  );
  const wholeAction = observations.filter(
    (item) => item.scope === 'whole_action' || item.scope === 'overall',
  );
  const unitSeries = observations.filter(
    (item) => item.scope === 'unit_series' || item.scope === 'unit',
  );
  const unitRows = groupUnitSeriesByPosition(unitSeries);
  const categoryGroups = groupObservationsByCategory(wholeAction);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!evaluationId) {
        message.error('缺少动作评估记录');
        setLoading(false);
        return;
      }
      try {
        const response = await getMemberTrainingWorkspaceActionDetail({
          requestData: { evaluationId },
        });
        if (!success(response)) {
          message.error(response?.msg || '动作评估详情查询失败');
          return;
        }
        if (!active) return;
        setDetail(response.data || null);
        if (response.data?.actionId) {
          getActionDetail({
            requestData: { actionId: response.data.actionId },
          })
            .then((actionResponse) => {
              if (active && success(actionResponse)) {
                setActionName(
                  actionResponse.data?.action?.shortName ||
                    actionResponse.data?.action?.actionName ||
                    actionResponse.data?.action?.name ||
                    '',
                );
              }
            })
            .catch(() => {
              // 动作名称是可选展示信息，失败时保留动作 ID，不影响评估详情。
            });
        }
      } catch (error) {
        message.error('动作评估详情暂时不可用');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [evaluationId]);

  useEffect(() => {
    setHasTraitMappings(null);
  }, [detail?.layer4ArtifactUrl]);

  if (loading) {
    return (
      <Card style={{ textAlign: 'center', padding: 64 }}>
        <Spin tip="正在加载动作评估" />
      </Card>
    );
  }

  const displayActionName =
    actionName || (detail?.actionId ? `动作 ${detail.actionId}` : '动作');
  const unavailableSections = detail
    ? [
        !detail.layer1ArtifactUrl && '骨骼回放',
        !detail.layer2ArtifactUrl &&
          !detail.layer3ArtifactUrl &&
          !detail.layer4ArtifactUrl &&
          '分层运动特性与信号图表',
      ].filter(Boolean)
    : [];
  const hasDiagnosticData =
    diagnosticSummary.length > 0 ||
    detail?.layer2ArtifactUrl ||
    detail?.layer3ArtifactUrl ||
    detail?.layer4ArtifactUrl;

  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      <Card>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.goBack()}
          style={{ marginBottom: 16 }}
        >
          返回会员训练档案
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {displayActionName}评估
        </Title>
        <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
          展示本次动作训练的客观观察、动作过程与评估结果。
        </Paragraph>
      </Card>

      {!detail ? (
        <Card>
          <Empty description="没有找到这次动作评估" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ display: 'flex' }}>
          <Card title="本次训练">
            <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
              <Descriptions.Item label="动作">
                {displayActionName}
              </Descriptions.Item>
              <Descriptions.Item label="评估状态">
                <Tag color={detail.status === 'failed' ? 'red' : 'blue'}>
                  {RECORD_STATUS_LABELS[detail.status] || '状态已记录'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="训练时间">
                {timeText(detail.ctime)}
              </Descriptions.Item>
              <Descriptions.Item label="所在课程">
                {detail.roomId ? `课程 ${detail.roomId}` : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card>
            {detail.status === 'failed' && (
              <Alert
                type="error"
                showIcon
                message="本次动作评估生成失败"
                description={
                  detail.failureMessage || '暂时没有可展示的评估数据'
                }
                style={{ marginBottom: 16 }}
              />
            )}

            {unavailableSections.length > 0 && (
              <Alert
                type="info"
                showIcon
                message="本次记录的数据完整度有限"
                description={`未随本次分析归档：${unavailableSections.join(
                  '、',
                )}。页面会继续展示已经形成的客观观察数据。`}
                style={{ marginBottom: 16 }}
              />
            )}

            {detail.layer1ArtifactUrl && (
              <Card
                size="small"
                title={
                  <Space>
                    <PlayCircleOutlined style={{ color: '#1890ff' }} />
                    骨骼动画回放
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <div style={{ height: 400 }}>
                  <KeypointPlayer
                    dataUrl={detail.layer1ArtifactUrl}
                    autoPlay
                    loop
                    onlyShow
                  />
                </div>
              </Card>
            )}

            <div style={{ marginBottom: 16 }}>
              <ActionInterpretationPanel
                interpretation={interpretation}
                status={detail.interpretationStatus}
                failureMessage={detail.interpretationFailureMessage}
                generatedAt={detail.interpretationGeneratedAt}
              />
            </div>

            {hasAiConclusion && (
              <div style={{ marginBottom: 16 }}>
                <AiConclusion conclusion={aiConclusion} />
              </div>
            )}

            {visibleSummary.length > 0 && (
              <Card
                size="small"
                title={
                  <Space>
                    <DashboardOutlined style={{ color: '#52c41a' }} />
                    动作完成情况
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                {renderMetricTiles(visibleSummary)}
              </Card>
            )}

            {wholeAction.length > 0 &&
              (!detail.layer4ArtifactUrl || hasTraitMappings === false) && (
                <Card
                  size="small"
                  title={
                    <Space>
                      <BarChartOutlined style={{ color: '#722ed1' }} />
                      动作表现观察
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: '100%' }}
                  >
                    {Object.entries(categoryGroups).map(([category, items]) => (
                      <div key={category}>
                        <Text
                          strong
                          style={{ display: 'block', marginBottom: 10 }}
                        >
                          {CATEGORY_LABELS[category] || '其他动作表现'}
                        </Text>
                        {renderMetricTiles(items)}
                      </div>
                    ))}
                  </Space>
                </Card>
              )}

            {hasDiagnosticData && (
              <Card
                size="small"
                title="诊断信息（数据质量、特征与波形）"
                style={{ marginBottom: unitRows.length > 0 ? 16 : 0 }}
              >
                <Paragraph type="secondary" style={{ marginTop: 0 }}>
                  以下为本次动作的客观测量依据，用于解释本次表现，不代表会员的长期运动能力评分。
                </Paragraph>
                {diagnosticSummary.length > 0 && (
                  <Card
                    size="small"
                    title="数据质量"
                    style={{ marginBottom: 16 }}
                  >
                    {renderMetricTiles(diagnosticSummary)}
                  </Card>
                )}
                {detail.layer4ArtifactUrl && (
                  <TraitMappingCards
                    layer4Url={detail.layer4ArtifactUrl}
                    onAvailabilityChange={setHasTraitMappings}
                  />
                )}
                {detail.layer3ArtifactUrl && (
                  <FeatureDetailCards layer3Url={detail.layer3ArtifactUrl} />
                )}
                {detail.layer2ArtifactUrl && (
                  <SignalWaveform layer2Url={detail.layer2ArtifactUrl} />
                )}
              </Card>
            )}

            {unitRows.length > 0 && (
              <Card size="small" title="逐次动作明细">
                <UnitSeriesCards rows={unitRows} />
              </Card>
            )}
          </Card>
        </Space>
      )}
    </Space>
  );
}
